import React from "react";
import { message, Modal } from "antd";
import {
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";

import { deleteAdvisee } from "apis/advisee";

import ClickableTextButton from "components/ClickableTextButton/ClickableTextButton";

import {
  buildAdviseesUri,
  buildAdviseePersonalInfoUri,
  buildAdviseePoliciesUri,
  buildAdviseeCashFlowUri,
  buildAdviseeInvestmentPortfolioUri,
  buildAdviseeNavReprogramUri,
  buildAdviseeActionPlanUri,
  buildAdviseeFutureReviewUri,
  buildPPISummaryUri,
  buildPPIDetailsUri,
  buildPPIReportingUri,
  buildPPSSummaryUri,
  buildPPSOwnedPoliciesUri,
  buildPPSAttachmentUri,
  buildPCFSummaryUri,
  buildPCFInflowsUri,
  buildPCFOutflowsUri,
  buildAdviseeEstatePlanningUri,
  buildPEPSummaryUri,
  buildPIPSummaryUri,
  buildPIPResourcesUri,
  buildPNRSummaryUri,
  buildPNRProposalsUri,
  buildPNRGptSolutionsUri,
  buildPAPSummaryUri,
  buildPFRSummaryUri,
  buildPPSOwnedPolicyDetailsUri,
  buildPNRProposalUri,
  buildPPIUserAttachmentUri,
} from "utils/routes";

import {
  ButtonsContainer,
  DeleteButtonContainer,
  DeleteIcon,
  DeleteText,
  NavButtonContainer,
  SubMenuItem,
  SubMenuItemContainer,
} from "./SubMenuItemSectionsBar.styles";

import Locale from "react-intl-universal";

const { confirm } = Modal;

const useGetBackLinkUri = (id) => {
  const isOwnPolicySubRoute = useRouteMatch(buildPPSOwnedPolicyDetailsUri());
  const isProposalSubRoute = useRouteMatch(buildPNRProposalUri());
  if (isOwnPolicySubRoute) {
    return {
      backText: Locale.get("subMenuItem.backText.backToPolicies"),
      backUrl: buildPPSOwnedPoliciesUri(id),
    };
  } else if (isProposalSubRoute) {
    return {
      backText: Locale.get("subMenuItem.backText.backToProposals"),
      backUrl: buildPNRProposalsUri(id),
    };
  }
  return {
    backText: Locale.get("subMenuItem.backText.backToAdvisees"),
    backUrl: buildAdviseesUri(),
  };
};

const SubMenuItemSectionsBar = () => {
  const history = useHistory();
  const { id } = useParams();
  const { pathname: currentLocation } = useLocation();

  const { backText, backUrl } = useGetBackLinkUri(id);

  const subMenuItemSections = [
    {
      isCurrentSection: currentLocation.startsWith(
        buildAdviseePersonalInfoUri(id)
      ),
      sections: [
        {
          label: Locale.get("subMenuItem.summarySection"),
          isActive: currentLocation.startsWith(buildPPISummaryUri(id)),
          onClick: () => history.push(buildPPISummaryUri(id)),
        },
        {
          label: Locale.get("subMenuItem.personalDetailSection"),
          isActive: currentLocation.startsWith(buildPPIDetailsUri(id)),
          onClick: () => history.push(buildPPIDetailsUri(id)),
        },
        {
          label: Locale.get("subMenuItem.reportingSection"),
          isActive: currentLocation.startsWith(buildPPIReportingUri(id)),
          onClick: () => history.push(buildPPIReportingUri(id)),
        },
        {
          label: Locale.get("subMenuItem.attachmentSection"),
          isActive: currentLocation.startsWith(buildPPIUserAttachmentUri(id)),
          onClick: () => history.push(buildPPIUserAttachmentUri(id)),
        },
      ],
    },
    {
      isCurrentSection: currentLocation.startsWith(buildAdviseePoliciesUri(id)),
      sections: [
        {
          label: Locale.get("subMenuItem.summarySection"),
          isActive: currentLocation.startsWith(buildPPSSummaryUri(id)),
          onClick: () => history.push(buildPPSSummaryUri(id)),
        },
        {
          label: Locale.get("subMenuItem.ownedPolicySection"),
          isActive: currentLocation.startsWith(buildPPSOwnedPoliciesUri(id)),
          onClick: () => history.push(buildPPSOwnedPoliciesUri(id)),
        },
        {
          label: Locale.get("subMenuItem.attachmentSection"),
          isActive: currentLocation.startsWith(buildPPSAttachmentUri(id)),
          onClick: () => history.push(buildPPSAttachmentUri(id)),
        },
      ],
    },
    {
      isCurrentSection: currentLocation.startsWith(buildAdviseeCashFlowUri(id)),
      sections: [
        {
          label: Locale.get("subMenuItem.summarySection"),
          isActive: currentLocation.startsWith(buildPCFSummaryUri(id)),
          onClick: () => history.push(buildPCFSummaryUri(id)),
        },
        {
          label: Locale.get("subMenuItem.cashInflowSection"),
          isActive: currentLocation.startsWith(buildPCFInflowsUri(id)),
          onClick: () => history.push(buildPCFInflowsUri(id)),
        },
        {
          label: Locale.get("subMenuItem.cashOutflowSection"),
          isActive: currentLocation.startsWith(buildPCFOutflowsUri(id)),
          onClick: () => history.push(buildPCFOutflowsUri(id)),
        },
      ],
    },
    {
      isCurrentSection: currentLocation.startsWith(
        buildAdviseeEstatePlanningUri(id)
      ),
      sections: [
        {
          label: Locale.get("subMenuItem.summarySection"),
          isActive: currentLocation.startsWith(buildPEPSummaryUri(id)),
          onClick: () => history.push(buildPEPSummaryUri(id)),
        },
      ],
    },
    {
      isCurrentSection: currentLocation.startsWith(
        buildAdviseeInvestmentPortfolioUri(id)
      ),
      sections: [
        {
          label: Locale.get("subMenuItem.summarySection"),
          isActive: currentLocation.startsWith(buildPIPSummaryUri(id)),
          onClick: () => history.push(buildPIPSummaryUri(id)),
        },
        {
          label: Locale.get("subMenuItem.resources"),
          isActive: currentLocation.startsWith(buildPIPResourcesUri(id)),
          onClick: () => history.push(buildPIPResourcesUri(id)),
        },
      ],
    },
    {
      isCurrentSection: currentLocation.startsWith(
        buildAdviseeNavReprogramUri(id)
      ),
      sections: [
        {
          label: Locale.get("subMenuItem.summarySection"),
          isActive: currentLocation.startsWith(buildPNRSummaryUri(id)),
          onClick: () => history.push(buildPNRSummaryUri(id)),
        },
        {
          label: Locale.get("subMenuItem.proposalSection"),
          isActive: currentLocation.startsWith(buildPNRProposalsUri(id)),
          onClick: () => history.push(buildPNRProposalsUri(id)),
        },
        {
          label: "GPT Solutions",
          isActive: currentLocation.startsWith(buildPNRGptSolutionsUri(id)),
          onClick: () => history.push(buildPNRGptSolutionsUri(id)),
        },
      ],
    },
    {
      isCurrentSection: currentLocation.startsWith(
        buildAdviseeActionPlanUri(id)
      ),
      sections: [
        {
          label: Locale.get("subMenuItem.actionPlanSection"),
          isActive: currentLocation.startsWith(buildPAPSummaryUri(id)),
          onClick: () => history.push(buildPAPSummaryUri(id)),
        },
      ],
    },
    {
      isCurrentSection: currentLocation.startsWith(
        buildAdviseeFutureReviewUri(id)
      ),
      sections: [
        {
          label: Locale.get("subMenuItem.futureReviewSection"),
          isActive: currentLocation.startsWith(buildPFRSummaryUri(id)),
          onClick: () => history.push(buildPFRSummaryUri(id)),
        },
      ],
    },
  ];

  const handleOnDeleteAdviseeButtonClick = () => {
    confirm({
      title: Locale.get("subMenuItem.confirmDeleteAdvisee"),
      okText: Locale.get("subMenuItem.yes"),
      cancelText: Locale.get("subMenuItem.no"),
      onOk() {
        deleteAdvisee(id)
          .then((res) => {
            message.success(Locale.get("subMenuItem.adviseeDeleted"));
            history.push(buildAdviseesUri());
          })
          .catch((ex) => {
            message.error(Locale.get("subMenuItem.errMsgDeleteAdvisee"));
          });
      },
      onCancel() {},
    });
  };

  return (
    <ButtonsContainer justify="space-between">
      <div>
        <NavButtonContainer>
          <ClickableTextButton
            text={backText}
            url={backUrl}
            type="back"
            showIcon
          />
        </NavButtonContainer>
        {subMenuItemSections.map(
          (subMenuItemSection) =>
            subMenuItemSection.isCurrentSection &&
            subMenuItemSection.sections.map((section) => (
              <SubMenuItem key={section.label}>
                <SubMenuItemContainer
                  isActive={section.isActive}
                  onClick={section.onClick}
                >
                  <p>{section.label}</p>
                </SubMenuItemContainer>
              </SubMenuItem>
            ))
        )}
      </div>
      <DeleteButtonContainer
        align="middle"
        justify="left"
        onClick={handleOnDeleteAdviseeButtonClick}
      >
        <DeleteIcon />
        <DeleteText>{Locale.get("subMenuItem.deleteAdvisee")}</DeleteText>
      </DeleteButtonContainer>
    </ButtonsContainer>
  );
};

export default SubMenuItemSectionsBar;
